/**
 * useUsersForSharing Hook
 * Hook for getting users for sharing purposes (uses dedicated for-sharing API accessible to all users)
 *
 * Refactored: Phase 3.4 - Factory Pattern Migration
 * Pattern: Uses createQueryHook factory for standardized query behavior
 * Previous: Manual useQuery pattern with helper function
 * Current: Factory pattern with inline error handling and wrapper for custom defaults
 *
 * Note: This uses the dedicated /api/users/for-sharing endpoint that bypasses the moderator-only GET /users/ endpoint
 */

import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHookNoParams } from './factories';

export interface UserForSharing {
  id: string;
  username: string;
  display_name?: string;  // full_name for nice display
  email?: string;         // accountname/mail for identification
  role?: string;          // user role
}

/**
 * Base hook created with factory
 */
const useUsersForSharingBase = createQueryHookNoParams<UserForSharing[]>({
  queryKey: () => ['users', 'sharing'] as const,
  queryFn: async (apiClient) => {
    log.api.request('GET', '/api/users/for-sharing', { purpose: 'sharing' });

    try {
      const response = await apiClient.getUsersForSharing({
        limit: 100
      });

      const users = Array.isArray(response.data?.results) ? response.data.results : [];

      log.api.response('GET', '/api/users/for-sharing', 200, { count: users.length });

      // Transform the search results to match our interface
      const transformedUsers = users.map((user: any) => ({
        id: user?.id ?? user?.username ?? '', // Use ObjectId as ID (consistent and unique)
        username: user?.username ?? '',
        display_name: user?.full_name ?? user?.display_name ?? user?.username ?? '', // Real name for display
        email: user?.email, // Account/mail for identification
        role: user?.role // User role
      }));

      // Debug logging
      if (transformedUsers.length > 0) {
        log.api.response('GET', '/api/users/for-sharing', 200, {
          count: transformedUsers.length,
          sample: transformedUsers[0]
        });
      }

      return transformedUsers;
    } catch (error: unknown) {
      log.api.error('GET', '/api/users/for-sharing', error as Error);
      // Return empty array on error - appropriate for sharing context
      return [];
    }
  },
  staleTime: QUERY_STALE_TIME.LONG, // 5 minutes - user list doesn't change often
});

/**
 * Hook to get users available for sharing (uses dedicated for-sharing API)
 * This bypasses the moderator-only GET /users/ endpoint by using the dedicated endpoint
 *
 * Special behavior:
 * - Returns empty array on error (graceful degradation for sharing context)
 * - Suppresses global error handling via meta.suppressGlobalError
 * - Disables retries to prevent multiple error dialogs
 * - Disables auto-refetching (window focus, reconnect, interval)
 * - Only fetches on mount
 *
 * @param optionsOrEnabled - Hook options object OR boolean (backward compatibility)
 * @example
 * ```typescript
 * const { data: users, isLoading } = useUsersForSharing();
 * const { data: users } = useUsersForSharing({ enabled: false }); // Object syntax
 * const { data: users } = useUsersForSharing(false); // Boolean syntax (backward compatible)
 * ```
 */
export const useUsersForSharing = (optionsOrEnabled?: { enabled?: boolean } | boolean) => {
  // Normalize the parameter - support both object and boolean for backward compatibility
  const enabled = typeof optionsOrEnabled === 'boolean'
    ? optionsOrEnabled
    : optionsOrEnabled?.enabled;

  return useUsersForSharingBase({
    enabled,
    queryOptions: {
      retry: false, // Disable retries to prevent multiple error dialogs
      refetchOnWindowFocus: false, // No refetch on window focus
      refetchOnReconnect: false, // No refetch on reconnect
      refetchOnMount: true, // Only fetch once on mount
      refetchInterval: false, // No background refetch - only manual refresh
      meta: {
        suppressGlobalError: true // Suppress errors from reaching global error handler
      }
    }
  });
};